import { Calendar, Clock, MapPin, Users, Video, Edit2 } from 'lucide-react';
import { Meeting } from '@/types/meeting';

interface MeetingCardProps {
  meeting: Meeting;
  onClick?: () => void;
  onEdit?: () => void;
}

export function MeetingCard({ meeting, onClick, onEdit }: MeetingCardProps) {
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div
      className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 flex-wrap flex-grow">
          <span className="px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800">
            {meeting.type.replace(/_/g, ' ')}
          </span>
          <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${
            meeting.status === 'SCHEDULED' ? 'bg-green-100 text-green-800 border border-green-200' :
            meeting.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
            meeting.status === 'COMPLETED' ? 'bg-gray-100 text-gray-800 border border-gray-200' :
            meeting.status === 'POSTPONED' ? 'bg-orange-100 text-orange-800 border border-orange-200' :
            'bg-red-100 text-red-800 border border-red-200'
          }`}>
            <span className={`w-2 h-2 mr-2 rounded-full ${
              meeting.status === 'SCHEDULED' ? 'bg-green-500' :
              meeting.status === 'IN_PROGRESS' ? 'bg-yellow-500' :
              meeting.status === 'COMPLETED' ? 'bg-gray-500' :
              meeting.status === 'POSTPONED' ? 'bg-orange-500' :
              'bg-red-500'
            }`} />
            {meeting.status.charAt(0) + meeting.status.slice(1).toLowerCase().replace('_', ' ')}
          </span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (onEdit) {
              onEdit();
            }
          }}
          className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50 transition-colors cursor-pointer"
          aria-label="Edit meeting"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>

      <h3 className="text-xl font-semibold mb-2">{meeting.title}</h3>
      <p className="text-gray-600 mb-4 line-clamp-2">{meeting.description}</p>
      
      <div className="space-y-2">
        <div className="flex items-center text-gray-600">
          <Calendar className="w-4 h-4 mr-2" />
          <span>{formatDate(meeting.startTime)}</span>
        </div>

        <div className="flex items-center text-gray-600">
          <Clock className="w-4 h-4 mr-2" />
          <span>{formatDate(meeting.endTime)}</span>
        </div>

        <div className="flex items-center text-gray-600">
          {meeting.isVirtual ? (
            <Video className="w-4 h-4 mr-2" />
          ) : (
            <MapPin className="w-4 h-4 mr-2" />
          )}
          <span>{meeting.isVirtual ? 'Virtual Meeting' : meeting.location}</span>
        </div>

        <div className="flex items-center text-gray-600">
          <Users className="w-4 h-4 mr-2" />
          <div className="flex -space-x-2">
            {meeting.participants.slice(0, 3).map((participant) => (
              <img
                key={participant.id}
                src={participant.avatar || `https://ui-avatars.com/api/?name=${participant.name}`}
                alt={participant.name}
                className="w-8 h-8 rounded-full border-2 border-white"
              />
            ))}
            {meeting.participants.length > 3 && (
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium">
                +{meeting.participants.length - 3}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}